import React, { useState } from "react";
import { Row, Col, Form, Input, Card, Button, Tooltip } from "antd";
import "./styles.css";
import { useSelector, useDispatch } from "react-redux";
import { PlusCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { numericInputValidator } from "../../../Common/Utils";
import {
  setCurrentStep,
  setRideDetails,
} from "../../../../reducers/testRideSlice";
import { TEST_RIDE, UNITS } from "../../../Common/Constants";
import TimeRangeForm from "./TimeRangeDetails";

const AfterRideForm = ({timeRangeValidation}) => {
  const dispatch = useDispatch();
  const afterRideFormRef = React.useRef(null);
  const rideDetails = useSelector((state) => state.testRide?.rideDetails);
  const [issuesList, setIssuesList] = useState(rideDetails?.issues || [""]);

  const onBack = () => {
    const { climate, level, ...inputValues } =
      afterRideFormRef.current?.getFieldValue();
    dispatch(
      setRideDetails({
        ...rideDetails,
        ...inputValues,
        issues: issuesList,
      })
    );
    dispatch(setCurrentStep(1));
  };

  const onFinish = (value) => {
    const { startDate, startTime, endDate, endTime } = value;
    const errorList = timeRangeValidation(startDate, startTime, endDate, endTime)
    if(errorList){
      afterRideFormRef?.current?.setFields(errorList);
    } else {
      dispatch(
        setRideDetails({ ...rideDetails, ...value, issues: issuesList })
      );
      dispatch(setCurrentStep(3));
    }
  };

  const observationChange = (index, value) => {
    let observation = [...issuesList];
    observation[index] = value;
    setIssuesList(observation);
  };

  const addissuesList = () => {
    setIssuesList((prev) => [...prev, ""]);
  };

  const removeissuesList = (index) => {
    let observation = [...issuesList];
    observation.splice(index, 1);
    setIssuesList(observation);
  };

  return (
    <Form
      name="afterRideForm"
      layout="vertical"
      ref={afterRideFormRef}
      onFinish={onFinish}
      initialValues={{
        ...rideDetails,
      }}
      validateMessages={{
        required: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
        whitespace: TEST_RIDE.FORM.REQUIRED_FIELDS.BLANK_ERROR,
      }}
      autoComplete="off"
    >
      <Row gutter={16}>
        <TimeRangeForm formRef={afterRideFormRef} />
        <Col xs={24} sm={24} md={24} lg={16} xl={16} xxl={18}>
          <Card bordered={false} className="input-box">
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="rideEndPlaceName"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.ENDING_LOCATION}
                >
                  <Input
                    title="rideEndPlaceName"
                    placeholder="Ending Location"
                    maxLength={50}
                    size="large"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="topSpeed"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.TOP_SPEED}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value),
                    },
                  ]}
                >
                  <Input
                    title="topSpeed"
                    placeholder="Top Speed"
                    maxLength={3}
                    size="large"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="endAmbient"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.AMBIENT} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Ambient Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="endMCU"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.MCU} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="MCU Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="endMotor"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.MOTOR} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Motor Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Item
                  name="endBattery"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.BATTERY} <span className="f12 grey">({UNITS.CELSIUS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Temperatures"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="endSOC"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.SOC} <span className="f12 grey">({UNITS.PERCENT})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Percentage"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="endVoltage"
                  label={<span>{TEST_RIDE.FORM.REQUIRED_FIELDS.VOLTAGE} <span className="f12 grey">({UNITS.VOLTS})</span></span>}
                  rules={[
                    {
                      message: "Enter a valid number",
                      validator: (_, value) => numericInputValidator(value, "decimalNegative"),
                    },
                  ]}
                >
                  <Input
                    size="large"
                    maxLength={6}
                    placeholder="Battery Voltage"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  name="comment"
                  className="mb0"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.COMMENTS}
                >
                  <Input.TextArea
                    size="large"
                    showCount
                    className="input-count-show-up"
                    maxLength={350}
                    rows={6}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12}>
                <Form.Item
                  className="mb0"
                  name="issues"
                  label={TEST_RIDE.FORM.REQUIRED_FIELDS.ISSUES}
                >
                  <div style={{ maxHeight: "160px", overflowY: "scroll" }}>
                    {issuesList.map((data, index) => (
                      <Row
                        key={index}
                        justify={"center"}
                        align="top"
                        wrap={true}
                      >
                        <Col flex="auto">
                          <Input
                            title="issues"
                            placeholder="Issues"
                            className="mb1"
                            maxLength={50}
                            onChange={(event) =>
                              observationChange(index, event.target.value)
                            }
                            value={data}
                            size="large"
                            allowClear
                          />
                        </Col>
                        <Col flex="30px" className="mt05">
                          {index + 1 === issuesList?.length ? (
                            <Tooltip title="Add">
                              <PlusCircleOutlined
                                className="input-add-icon"
                                onClick={addissuesList}
                              />
                            </Tooltip>
                          ) : (
                            <Tooltip title="Remove">
                              <CloseCircleOutlined
                                className="input-close-icon"
                                onClick={() => removeissuesList(index)}
                              />
                            </Tooltip>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </div>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Col span={24}>
        <Form.Item
          className="mt2"
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Row>
            <Button
              role="BackBtn"
              className="back-btn"
              htmlType="button"
              style={{ width: "80px" }}
              onClick={onBack}
            >
              Back
            </Button>
            <Button
              data-testid="submitBtn"
              type="primary"
              className="submit-btn"
              htmlType="submit"
              style={{ width: "80px", marginLeft: "2rem" }}
            >
              Next
            </Button>
          </Row>
        </Form.Item>
      </Col>
    </Form>
  );
};

export default AfterRideForm;
